import {
  CreateEventInput,
  RecurringEventFrequency,
  UpdateEventInput,
} from '@tribeplatform/gql-client/types'
import { DateValue } from '@tribeplatform/react-ui-kit/DateTimePicker'

import { dayjs } from '../../common/lib/dayjs.js'
import { I18nShape } from '../../i18n/types.js'
import { EventFormFields } from '../types.js'

export const frequencyItemsFactory = ({ $t }: I18nShape) => [
  {
    text: $t({
      id: 'Event.Form.Recurrence.DoesNotRepeat',
      defaultMessage: 'Does not repeat',
    }),
    value: RecurringEventFrequency.once,
  },
  {
    text: $t({
      id: 'Generics.Daily',
      defaultMessage: 'Daily',
    }),
    value: RecurringEventFrequency.daily,
  },
  {
    text: $t({
      id: 'Generics.Weekly',
      defaultMessage: 'Weekly',
    }),
    value: RecurringEventFrequency.weekly,
  },
  {
    text: $t({
      id: 'Generics.Monthly',
      defaultMessage: 'Monthly',
    }),
    value: RecurringEventFrequency.monthly,
  },
  {
    text: $t({
      id: 'Generics.Yearly',
      defaultMessage: 'Yearly',
    }),
    value: RecurringEventFrequency.yearly,
  },
]

export const createUpdateEventInput = (
  defaultValues: EventFormFields,
  updatedValues: Partial<EventFormFields>,
) => {
  const { recurrenceConfig } = updatedValues

  let startTime: string | undefined
  if (updatedValues.startTime || updatedValues.timeZone) {
    startTime = dayjs(updatedValues.startTime ?? defaultValues.startTime)
      .tz(updatedValues.timeZone ?? defaultValues.timeZone, true)
      .toISOString()
  }
  let endTime: string | undefined
  if (updatedValues.endTime || updatedValues.timeZone) {
    endTime = dayjs(updatedValues.endTime ?? defaultValues.endTime)
      .tz(updatedValues.timeZone ?? defaultValues.timeZone, true)
      .toISOString()
  }

  /**
   * The b/e requires the thumbnailId to be null if the thumbnail is removed.
   */
  const thumbnailId =
    updatedValues.thumbnail !== null ? updatedValues.thumbnail?.id : null

  /**
   * We do not have support for polls and tags in the event form yet.
   * @todo: add support for polls and tags in the event form when it is implemented.
   */
  return {
    thumbnailId,
    notificationSettings: updatedValues.notificationSettings,
    slug: updatedValues.slug,
    timeZone: updatedValues.timeZone,
    title: updatedValues.title,
    content: updatedValues.content,
    hostIds: updatedValues.hosts?.map(host => host.id),
    endTime,
    startTime,
    physicalLocations: updatedValues.physicalLocations
      ? updatedValues.physicalLocations?.map(location => ({
          latitude: location.latitude,
          longitude: location.longitude,
          placeId: location.placeId ?? '',
          displayName: location.displayName,
          formattedAddress: location.formattedAddress,
          plusCode: location.plusCode,
          additionalData: location.additionalData,
        }))
      : undefined,
    capacity: updatedValues.capacity
      ? Number(updatedValues.capacity)
      : undefined,
    customSeoDetail: updatedValues.customSeoDetail
      ? {
          title: updatedValues.customSeoDetail.title,
          description: updatedValues.customSeoDetail.description,
          thumbnailId: updatedValues.customSeoDetail.thumbnail?.id ?? null,
        }
      : undefined,

    virtualLocations: updatedValues.virtualLocations?.map(location => ({
      url: location.url,
      provider: location.provider,
    })),
    recurrenceConfig: recurrenceConfig
      ? {
          frequency: recurrenceConfig.frequency,
          recurrenceEndDate: recurrenceConfig.recurrenceEndDate
            ? dayjs(recurrenceConfig.recurrenceEndDate).toISOString()
            : undefined,
          repeatsOn: recurrenceConfig.repeatsOn,
        }
      : undefined,
  } satisfies UpdateEventInput
}

export const createCreateEventInput = (data: EventFormFields) => {
  return {
    content: data.content,
    hostIds: data.hosts?.map(host => host.id) ?? [],
    notificationSettings: {
      notifyOnDelete: data.notificationSettings?.notifyOnDelete ?? true,
      notifyOnRegister: data.notificationSettings?.notifyOnRegister ?? true,
      notifyOnUpdate: data.notificationSettings?.notifyOnUpdate ?? true,
      remindBefore: data.notificationSettings?.remindBefore ?? true,
    },
    startTime: dayjs(data.startTime).tz(data.timeZone, true).toISOString(),
    endTime: dayjs(data.endTime).tz(data.timeZone, true).toISOString(),
    physicalLocations: data.physicalLocations?.map(location => ({
      latitude: location.latitude,
      longitude: location.longitude,
      placeId: location.placeId ?? '',
      displayName: location.displayName,
      formattedAddress: location.formattedAddress,
      plusCode: location.plusCode,
      additionalData: location.additionalData,
    })),
    timeZone: data.timeZone,
    title: data.title,
    capacity: data.capacity ? Number(data.capacity) : undefined,
    customSeoDetail: data.customSeoDetail
      ? {
          title: data.customSeoDetail.title,
          description: data.customSeoDetail.description,
          thumbnailId: data.customSeoDetail.thumbnail?.id ?? null,
        }
      : undefined,

    virtualLocations: data.virtualLocations?.map(location => ({
      url: location.url,
      provider: location.provider,
    })),
    thumbnailId: data.thumbnail?.id ?? null,
    slug: data.slug,
    recurrenceConfig: data.recurrenceConfig,
  } satisfies CreateEventInput
}

export const isSameDateTime = (date1: DateValue, date2: DateValue) => {
  const d1 = dayjs(date1).utc()
  const d2 = dayjs(date2).utc()
  return (
    d1.minute() === d2.minute() &&
    d1.hour() === d2.hour() &&
    d1.date() === d2.date() &&
    d1.month() === d2.month() &&
    d1.year() === d2.year()
  )
}
